// runEditMotionScene — the gated executor for the agent's `editMotionScene`
// mutation. Called by BulkMutateToolUI on the user's Apply click, never
// auto-applied.
//
// This is the ONLY way a motion scene changes after it is generated: there is
// no edit UI and no regenerate path, because a regeneration throws away a
// scene the author already accepted. The user stands somewhere on the
// timeline, says what is wrong with the frame in front of them, and the
// backend renders exactly that frame for the model to look at.
//
// Returns { jobId, boxId } so the card can follow the job on the existing
// session-jobs socket. The completed job carries a NEW document url — applied
// by applyMotionEdit() below, which points the box at it. The previous
// document is left in storage, so an edit that makes the scene worse costs one
// undo rather than a regeneration.

import { getToken } from "../../../common/environment_config";
import { updateBox } from "../../../common/actions.es6";

const MOTION_PLUGIN_ID = "MotionScene";

// The editor's training id, from `?id=` — the same room key the sibling
// runners and useSessionImageJobs use.
function readTrainingId() {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get("id") || null;
  } catch {
    return null;
  }
}

/**
 * The motion box on a scene, if it has one.
 *
 * pluginToolbarsById is the authoritative source for plugin identity and for
 * plugin state; boxesById.state can lag behind it, and reading the stale copy
 * here would send the model a document the user is no longer looking at.
 *
 * Returns { boxId, htmlUrl, version, durationSec } or null.
 */
export function findMotionBox(present, navId) {
  const navItem = present && present.navItemsById && present.navItemsById[navId];
  const boxIds = (navItem && Array.isArray(navItem.boxes) && navItem.boxes) || [];
  const toolbars = (present && present.pluginToolbarsById) || {};
  const boxesById = (present && present.boxesById) || {};

  for (const boxId of boxIds) {
    const toolbar = toolbars[boxId];
    const box = boxesById[boxId];
    const pluginId = (toolbar && toolbar.pluginId) || (box && box.pluginId);
    if (pluginId !== MOTION_PLUGIN_ID) continue;
    const state = (toolbar && toolbar.state) || (box && box.state) || {};
    if (!state.htmlUrl) continue;
    return {
      boxId,
      htmlUrl: state.htmlUrl,
      version: typeof state.version === "number" ? state.version : 1,
      durationSec: typeof state.durationSec === "number" ? state.durationSec : null,
      // The model that WROTE this scene. The edit runs on the same one: a
      // scene written by GPT-6 and edited by Opus comes back in a different
      // hand, in work the author had already accepted. Absent on boxes made
      // before the field existed — the backend falls back there.
      model: typeof state.model === "string" ? state.model : null,
    };
  }
  return null;
}

/**
 * Start one edit.
 *
 * @param {Function} _dispatch unused here (parity with the executor signature)
 * @param {Function} getState  returns the editor Redux state
 * @param {Object} params      { instruction, tSec }  tSec is SCENE-LOCAL
 * @param {Object} scope       { navId }
 * @returns {Promise<{ jobId?: string, boxId?: string, error?: string }>}
 */
export async function runEditMotionScene(_dispatch, getState, params, scope) {
  const state = typeof getState === "function" ? getState() : null;
  const present = state && state.undoGroup && state.undoGroup.present;
  const navId = scope && scope.navId;
  if (!present || !navId) return { error: "No scene is open." };

  const motion = findMotionBox(present, navId);
  if (!motion) return { error: "This scene has no motion scene to edit." };

  const body = new FormData();
  body.append("htmlUrl", motion.htmlUrl);
  body.append("tSec", String(params && params.tSec != null ? params.tSec : 0));
  body.append("instruction", (params && params.instruction) || "");
  body.append("navId", navId);
  body.append("version", String(motion.version));
  if (motion.model) body.append("llmModel", motion.model);
  const trainingId = readTrainingId();
  if (trainingId) body.append("idealStudioTrainingId", trainingId);

  try {
    const response = await fetch(`${process.env.MW_API_URL}api/infographic-scene/motion-edit`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body,
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      return { error: (errData.message || `HTTP ${response.status}`).toString() };
    }
    const result = await response.json();
    if (!result || !result.jobId) return { error: "No jobId in response" };
    return { jobId: result.jobId, boxId: motion.boxId };
  } catch (err) {
    return { error: (err && err.message) || String(err) };
  }
}

/**
 * Point the box at the edited document.
 *
 * Only the document url and its version change: the timeline, the position and
 * the label are the author's and an edit has no business touching them. The
 * duration is written through only when the backend reports one, and it should
 * always be the same — the edit prompt forbids moving the scene off its audio.
 *
 * @param {Function} dispatch  Redux dispatch
 * @param {Function} getState  returns the editor Redux state
 * @param {string} boxId
 * @param {Object} result      the completed job's result
 */
export function applyMotionEdit(dispatch, getState, boxId, result) {
  if (!dispatch || !boxId || !result || !result.htmlUrl) return false;
  const state = typeof getState === "function" ? getState() : null;
  const present = state && state.undoGroup && state.undoGroup.present;
  const toolbar = present && present.pluginToolbarsById && present.pluginToolbarsById[boxId];
  if (!toolbar) return false;

  const next = {
    ...(toolbar.state || {}),
    htmlUrl: result.htmlUrl,
    version: typeof result.version === "number" ? result.version : undefined,
  };
  if (typeof result.durationSec === "number") next.durationSec = result.durationSec;
  // `version: undefined` would be written as a key; drop it instead.
  if (next.version === undefined) delete next.version;

  dispatch(updateBox(boxId, "", toolbar, next));
  return true;
}

export default runEditMotionScene;
